import { useEffect, useMemo, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";

export default function SuperAdultControl() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/platform/adult-control")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch((e) => toast.error(extractErr(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((t) =>
      `${t.name || ""} ${t.slug || ""} ${t.owner_email || ""}`.toLowerCase().includes(s)
    );
  }, [items, q]);

  const toggle = async (t, enabled) => {
    setSaving((m) => ({ ...m, [t.id]: true }));
    try {
      const { data } = await api.put(`/platform/tenants/${t.id}/adult-control`, { enabled });
      setItems((list) => list.map((x) => (x.id === t.id ? { ...x, ...data } : x)));
      toast.success(enabled ? `18+ consent on for ${t.name}` : `18+ consent off for ${t.name}`);
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving((m) => ({ ...m, [t.id]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="adult-control-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-white flex items-center gap-2">
          <ShieldAlert className="w-6 h-6 text-red-400" /> Adult Control
        </h1>
        <p className="text-sm text-slate-400 mt-1">Turn on 18+ consent for any tenant. Shoppers must confirm they are adults before they can browse or buy.</p>
      </div>
      <Card className="p-4 bg-slate-950/50 border-slate-800">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search store, slug, or owner email"
          className="bg-slate-900 border-slate-700 text-white"
          data-testid="adult-control-search"
        />
      </Card>
      <Card className="overflow-hidden bg-slate-950/50 border-slate-800">
        {loading ? (
          <div className="p-8 text-slate-400 text-sm">Loading tenants…</div>
        ) : (
          <div className="divide-y divide-slate-800">
            {filtered.length === 0 ? (
              <div className="p-8 text-slate-400 text-sm">No tenants match.</div>
            ) : filtered.map((t) => (
              <div key={t.id} className="flex items-center gap-4 px-4 py-3" data-testid={`adult-control-row-${t.slug || t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-white truncate">{t.name}</div>
                  <div className="text-xs text-slate-500 truncate">{t.slug}{t.owner_email ? ` · ${t.owner_email}` : ""}</div>
                </div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 shrink-0 hidden sm:block">{t.platform_enforced ? "Forced on" : t.enabled ? "On" : "Off"}</div>
                <Switch
                  checked={Boolean(t.enabled)}
                  disabled={Boolean(saving[t.id])}
                  onCheckedChange={(v) => toggle(t, v)}
                  data-testid={`adult-control-switch-${t.slug || t.id}`}
                />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
